import { timeParse, timeFormat } from 'd3';
import { cloneDeep } from 'lodash';
import { dateService } from '../../../services/date';

function aggregateStats(allStats, granularity, xTicks, merchantPerformance = false) {
  /*
   * Create buckets that represent the time period between each set of 2 ticks.
   * As we iterate through the stats, we place them in the bucket they belong
   * in by comparing dates. Then, aggregate each bucket of stats together.
   */
  const aggregateStats = [];

  allStats.forEach(obj => {
    const dateBuckets = xTicks.map(tick => {
      return {
        date: tick,
        stats: [],
      };
    });

    const stats = obj.data;
    let bucketIndex = 0;

    for (let i = 0; i < stats.length; i++) {
      const currentDate = dateBuckets[bucketIndex].date;
      const nextDate = dateBuckets[bucketIndex + 1] ? dateBuckets[bucketIndex + 1].date : null;
      const currentDateComparison = dateService.compareDates(currentDate, stats[i].event_date);
      const nextDateComparison = dateService.compareDates(nextDate, stats[i].event_date);

      if (currentDateComparison <= 0 && (nextDateComparison > 0 || !nextDate)) {
        dateBuckets[bucketIndex].stats.push(stats[i]);
      } else {
        bucketIndex++;
        i--;
      }
    }
    aggregateStats.push(aggregateByDate(dateBuckets, merchantPerformance));
  });

  return allStats.map((obj, i) => {
    obj.data = aggregateStats[i];
    return obj;
  });
}

function aggregateByDate(buckets, merchantPerformance) {
  const formatDate = timeFormat('%Y-%m-%d');
  const statArray = buckets.map(bucket => {
    if (!bucket.stats.length) return null;

    const { stats } = bucket;
    const aggregateStat = key =>
      stats.reduce((agg, stat) => {
        return (agg += parseFloat(stat[key]));
      }, 0);
    if (merchantPerformance) {
      const revenueKey = stats[0] && stats[0].attributed_revenue ? 'attributed_revenue' : 'revenue';
      return {
        event_date: formatDate(bucket.date),
        [revenueKey]: aggregateStat(revenueKey),
        spend: aggregateStat('spend'),
        clicks: aggregateStat('clicks'),
        rpc: aggregateStat('revenue') / aggregateStat('clicks'),
        roi_actual: parseFloat(aggregateStat('roi_actual') / stats.length).toFixed(1),
      };
    }
    return {
      pub_id: stats[0].pub_id,
      event_date: formatDate(bucket.date),
      clicks: aggregateStat('clicks'),
      publisher_total_earnings: aggregateStat('publisher_total_earnings'),
      total_attributed_revenue: aggregateStat('total_attributed_revenue'),
    };
  });
  return statArray.filter(data => data);
}

function getTicks(granularity, dateFrom, dateTo) {
  /*
   * By determining the x-axis ticks in megatable-graph,
   * we can more clearly aggregate the stats
   */
  const xTicks = [dateFrom];
  const offsetUnit = granularity === 'daily' ? 'days' : `${granularity.slice(0, -2)}s`;
  const parseDate = timeParse('%Y-%m-%d');

  let date = dateService.offsetFromDate(dateFrom, 1, offsetUnit);

  // Continue to add ticks until we've exceeded our date range
  while (dateService.compareDates(date, dateTo) <= 0) {
    const adjustedDate = getGranularityStartDate(cloneDeep(date), granularity);
    xTicks.push(adjustedDate);
    date = dateService.offsetFromDate(date, 1, offsetUnit);
  }

  return xTicks.map(date => parseDate(date));
}

function getGranularityStartDate(dateObject, granularity) {
  // weekly => week, monthly => month, yearly => year
  const timeUnit = granularity.slice(0, -2);
  return dateService.formatDate(dateObject.startOf(timeUnit).startOf('day'));
}

function processStats(stats, ticks, selectedGranularity, dateRange, option) {
  // Backend returns all monthly stats rounded down to the 1st, replace with correct date
  if (['monthly', 'yearly'].includes(selectedGranularity)) {
    const formattedDate = dateRange.date_from;
    const truncatedDateFrom = formattedDate.slice(0, 7);
    const truncatedStatDate = stats[0].event_date.slice(0, 7);

    if (truncatedDateFrom === truncatedStatDate) {
      // Ex: 2019-04-01 becomes 2019-04-08
      stats[0].event_date = formattedDate;
    }
  }

  let processedStats = [{ data: stats, color: option.color }];
  if (['weekly', 'yearly'].includes(selectedGranularity)) {
    processedStats = aggregateStats(processedStats, selectedGranularity, ticks, true);
  }
  return processedStats;
}

function processEditGraphStats(respData, ticks, selectedGranularity, dateRange) {
  let processedStats = respData.map(obj => {
    if (['monthly', 'yearly'].includes(selectedGranularity)) {
      const formattedDate = dateRange.date_from;
      const truncatedDateFrom = formattedDate.slice(0, 7);
      const truncatedStatDate = obj.data[0].event_date.slice(0, 7);

      if (truncatedDateFrom === truncatedStatDate) {
        // Ex: 2019-04-01 becomes 2019-04-08
        obj.data[0].event_date = formattedDate;
      }
    }
    return obj;
  });

  if (['weekly', 'yearly'].includes(selectedGranularity)) {
    processedStats = aggregateStats(processedStats, selectedGranularity, ticks, true);
  }
  return processedStats;
}

export { aggregateStats, getTicks, getGranularityStartDate, processStats, processEditGraphStats };
